import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { clearDailyStats, getDailyStats } from 'actions/superuser';
import { Line } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import 'chartjs-plugin-colorschemes';

import './DailyStats.css';
import { DateTimePicker } from 'elements';
import { transformDailyStats } from '../../utils/stat_utils';
import moment from 'moment';

function mapState(state) {
  return {
    ...state.superuser
  };
}

function mapDispatch(dispatch, props) {
  return {
    clearDailyStats() {
      return clearDailyStats(dispatch);
    },
    getDailyStats(data) {
      return getDailyStats(dispatch, data);
    }
  };
}

export const dailyStatsChartPageType = {
  sales: 'sales',
  suggestions: 'suggestions'
};

class DailyStats extends Component {

  state = {
    dateStart: moment().startOf('day').subtract(30, 'day'),
    dateEnd: moment().startOf('day')
  };

  static propTypes = {
    pt: PropTypes.string,
    envStats: PropTypes.object,
    clearDailyStats: PropTypes.func,
    getDailyStats: PropTypes.func
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.pt !== nextProps.pt) {
      this.props.clearDailyStats();
      this.setState({ dateStart: null, dateEnd: null });
    }
  }

  componentWillUnmount() {
    this.props.clearDailyStats();
  }

  componentWillMount() {
    this.handleStatChange(this.state.dateStart, this.state.dateEnd);
  }

  handleDateRangeChange(dateStart, dateEnd) {
    this.setState({ dateEnd, dateStart });
    this.handleStatChange(dateStart, dateEnd);
  }

  handleStatChange(dateStart, dateEnd) {
    if (dateStart && dateEnd) {
      const data = {
        dateEnd: dateEnd.format(),
        dateStart: dateStart.format('YYYY-MM-DD HH:mm:ss')
      };
      this.props.clearDailyStats();
      this.props.getDailyStats(data);
    }
  }

  get selectDateRange() {
    const { dateStart, dateEnd } = this.state;
    return <DateTimePicker
      value={ [dateStart, dateEnd] }
      exportData={ (a, b) => this.handleDateRangeChange(a, b) }
    />;
  }

  get chartHeight() {
    return 600;
  }

  get pageTitle() {
    switch (this.props.pt) {
      case dailyStatsChartPageType.suggestions:
        return 'Psychic bot data';
      case dailyStatsChartPageType.sales:
      default:
        return 'Sales data';
    }
  }

  get charts() {
    const { dailyStats, pt } = this.props;
    const [transformedDailyStats, labels] = transformDailyStats(dailyStats);
    const rawEnvs = Object.keys(transformedDailyStats);
    const envs = rawEnvs.sort();

    const dataSetsNFree = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: i.nFree
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const dataSetsNPaid = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: i.nPaid
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const dataSetsRevenue = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: i.revenue
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const dataSetsNReplies = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: i.nReplies
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const dataRepliesWithSuggestion = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: i.nRepliesWithSuggestions
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const dataRepliesWithSuggestionRelative = envs.map(i => ({
      data: transformedDailyStats[i].stats ? transformedDailyStats[i].stats.map(i => ({
        x: i.x,
        y: 100 * i.nRepliesWithSuggestions / i.nReplies || 0
      })) : [],
      label: i,
      backgroundColor: 'rgba(255,255,255,0)'
    }));

    const freeMessagesData = { datasets: dataSetsNFree, labels };
    const paidMessagesData = { datasets: dataSetsNPaid, labels };
    const revenueData = { datasets: dataSetsRevenue, labels };

    const repliesData = { datasets: dataSetsNReplies, labels };
    const repliesWithSuggestionData = { datasets: dataRepliesWithSuggestion, labels };
    const repliesWithSuggestionRelativeData = { datasets: dataRepliesWithSuggestionRelative, labels };


    if (envs.length <= 1) return [];
    switch (pt) { // page type
      case dailyStatsChartPageType.sales:
        return [
          [freeMessagesData, 'Free Messages'],
          [paidMessagesData, 'Paid Messages'],
          [revenueData, 'Revenue']
        ];
      case dailyStatsChartPageType.suggestions:
        return [
          [repliesData, 'Replies'],
          [repliesWithSuggestionData, 'Suggestion used'],
          [repliesWithSuggestionRelativeData, 'Suggestion used (relative, %)']
        ];
    }
  }

  render() {
    const { dateStart, dateEnd } = this.state;

    return (
      <div className="stat-container">
        <div className="page-title">{ this.pageTitle }</div>
        { this.selectDateRange }
        { (dateStart && dateEnd) &&
        <div className="env-stats">
          <div>
            from: { dateStart.format('YYYY/MM/DD HH:mm:ss') }
          </div>
          <div>
            to: { dateEnd.format('YYYY/MM/DD HH:mm:ss') }
          </div>
        </div>
        }
        { this.charts
          .map((v, i) => (
            <div key={ i }>
              <Line
                data={ v[0] }
                height={ this.chartHeight }
                options={ {
                  maintainAspectRatio: false,
                  plugins: {
                    colorschemes: {
                      scheme: 'tableau.Classic20'
                    }
                  },
                  legend: {
                    display: true,
                    position: 'left',
                    align: 'start'
                  },
                  elements: {
                    line: {
                      tension: 0
                    }
                  },
                  title: {
                    display: true,
                    text: v[1]
                  },
                  scales: {
                    xAxes: [{
                      ticks: {
                        beginAtZero: true
                      }
                    }],
                    yAxes: [{
                      ticks: {
                        beginAtZero: true
                      }
                    }]
                  }
                } }
              />
            </div>))
        }
      </div>
    );
  }
}

export default withRouter(connect(mapState, mapDispatch)(DailyStats));



// WEBPACK FOOTER //
// ./src/pages/daily_stats/DailyStats.jsx